import { http } from '../request'
import { fetchAllPages, type PageResponse } from '@/utils/pagination'
import {
  type ManagementEntity,
  type ManagementImportResult,
  normalizeListResponse,
  normalizePageResponse
} from './shared'
import type { CourseTypeCode } from '../subject-config'

export interface SubjectDto extends ManagementEntity {
  name: string
  isRequired: boolean
  credit?: number
  sort: number
  courseCount: number
}

export interface SubjectCourseDto {
  id: number
  subjectId: number | null
  subjectName: string
  courseTypeId: number
  courseTypeName: string
  courseTypeCode: CourseTypeCode
  name: string
  isRequired?: boolean
  credit?: number
  hours?: number
  sort: number
}

type SubjectPayload = {
  name: string
  isRequired: boolean
  credit: number | undefined
  sort?: number
}

function toSubjectPayload(payload: unknown): SubjectPayload {
  const subject = payload as Partial<SubjectDto>
  return {
    name: String(subject.name ?? '').trim(),
    isRequired: subject.isRequired ?? true,
    credit: subject.credit,
    sort: subject.sort
  }
}

async function listAllSubjects(): Promise<SubjectDto[]> {
  return fetchAllPages<SubjectDto>({
    fetchFn: (params) => http.get<PageResponse<SubjectDto>>('/data-management/subjects', params),
    normalizeFn: (raw) => normalizePageResponse<SubjectDto>(raw)
  })
}

export const subjectApi = {
  async list(): Promise<SubjectDto[]> {
    return listAllSubjects()
  },
  async listAll(): Promise<SubjectDto[]> {
    const response = await http.get<SubjectDto[]>('/data-management/subjects/all')
    return normalizeListResponse<SubjectDto>(response.data)
  },
  async create(payload: unknown): Promise<SubjectDto> {
    const response = await http.post<SubjectDto>('/data-management/subjects', toSubjectPayload(payload))
    return response.data
  },
  async update(id: number, payload: unknown): Promise<SubjectDto> {
    const response = await http.put<SubjectDto>(`/data-management/subjects/${id}`, toSubjectPayload(payload))
    return response.data
  },
  async remove(id: number): Promise<void> {
    await http.delete<void>(`/data-management/subjects/${id}`)
  },
  async batchRemove(ids: number[]): Promise<void> {
    await http.post<void>('/data-management/subjects/batch-delete', { ids })
  },
  async importRows(rows: unknown[]): Promise<ManagementImportResult> {
    const response = await http.post<ManagementImportResult>(
      '/data-management/subjects/import',
      rows.map(toSubjectPayload)
    )
    return response.data
  },
  async listCoursesBySubject(subjectId?: number | null): Promise<SubjectCourseDto[]> {
    const response = await http.get<SubjectCourseDto[]>('/data-management/subjects/courses', { subjectId: subjectId ?? undefined })
    return normalizeListResponse<SubjectCourseDto>(response.data)
  },
  async listAllCourses(courseTypeCode?: CourseTypeCode): Promise<SubjectCourseDto[]> {
    const response = await http.get<SubjectCourseDto[]>('/data-management/subjects/courses/all', { courseTypeCode })
    return normalizeListResponse<SubjectCourseDto>(response.data)
  },
  async batchListCoursesBySubjectIds(subjectIds: number[]): Promise<Record<number, SubjectCourseDto[]>> {
    if (subjectIds.length === 0) return {}
    const response = await http.get<Record<number, SubjectCourseDto[]>>('/data-management/subjects/courses/batch-by-subjects', { subjectIds })
    return (response.data ?? {}) as Record<number, SubjectCourseDto[]>
  },
  async createCourse(payload: { subjectId?: number | null; courseTypeId: number; name: string; isRequired?: boolean; credit?: number; hours?: number; sort?: number }): Promise<SubjectCourseDto> {
    const response = await http.post<SubjectCourseDto>('/data-management/subjects/courses', payload)
    return response.data
  },
  async updateCourse(id: number, payload: { name?: string; isRequired?: boolean; credit?: number; hours?: number; sort?: number; courseTypeId?: number; subjectId?: number | null }): Promise<SubjectCourseDto> {
    const response = await http.put<SubjectCourseDto>(`/data-management/subjects/courses/${id}`, payload)
    return response.data
  },
  async removeCourse(id: number): Promise<void> {
    await http.delete<void>(`/data-management/subjects/courses/${id}`)
  }
}
